import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Feature } from 'config/features';
import { Provider as FormProvider } from 'jotai';

import { useSync } from 'hooks/form/useSync';
import { useFormValidation } from 'hooks/form/useValidation';
import { useFeatureList } from 'hooks/useFeature';
import { QaIdentifier, useQa } from 'hooks/useQa';

import { formStore } from 'data/Atom/Store';

import { DatepickerType } from '../Datepicker/types';

import { ToggleFormElementProvider } from './__context/toggleFormElementProvider';
import Airport from './__partials/Airport';
import Datepicker from './__partials/Datepicker/index';
import Destination from './__partials/Destination';
import DurationLayer from './__partials/Duration';
import HighlightInitiator from './__partials/HighlightInitiator';
import OccupancyLayer from './__partials/Occupancy';
import SubmitContainer from './__partials/Submit';
import Toggle from './__partials/Toggle';
import { InitialProps } from './types';

import styles from './SingleLine.module.scss';

const SingleLine = memo(function MultiLine({
    formType,
    initialValue,
    onSubmit,
}: InitialProps) {
    const { t } = useTranslation();
    const { syncSearchWithFormStore, syncFilterAndSearchWithFormStore } =
        useSync();
    const { isFormValidate, onValidateItem, onValidateForm, isSubmitPossible } =
        useFormValidation();
    const [isManualFilterSubmitActive, isS1S4MandatoryActive] = useFeatureList(
        Feature.ManualFilterSubmit,
        Feature.S1S4Mandatory
    );
    const qaAttr = useQa(QaIdentifier.Search_Form_Container);

    const onSubmit_ = useCallback(() => {
        // if destination is changed, sync the filter from search
        onValidateForm();
        if (isSubmitPossible()) {
            if (isManualFilterSubmitActive) {
                syncFilterAndSearchWithFormStore();
            } else {
                syncSearchWithFormStore();
            }
            onSubmit?.();
        }
    }, [
        isManualFilterSubmitActive,
        onSubmit,
        syncFilterAndSearchWithFormStore,
        syncSearchWithFormStore,
        onValidateForm,
        isSubmitPossible,
    ]);

    return (
        <FormProvider store={formStore}>
            <HighlightInitiator />
            <div
                className={classNames(styles.FormContainer, 'search-box-cnt')}
                {...qaAttr}
            >
                <Toggle
                    customClassList={{
                        ToggleWrapper: styles.ToggleWrapper,
                        Toggle: styles.Toggle,
                        Inactive: styles.Inactive,
                    }}
                    hasToolTip={false}
                    isFewoToggleActive={false}
                    isChipStyle={true}
                />
                <div className={styles.FormWrapper}>
                    <div className={styles.Destination}>
                        <Destination
                            initialValue={initialValue?.destination}
                            onValidate={onValidateItem}
                            onSubmit={onSubmit_}
                            placeholder={t('TRANS-all-destinations')}
                            label={t('TRANS-destination-or-hotel')}
                            customClassList={{
                                InputWrapper: classNames(styles.InputWrapper, {
                                    [styles.Error]:
                                        isFormValidate.isDestinationEmpty,
                                }),
                                Input: styles.Input,
                                Label: styles.Label,
                                Layer: styles.DestinationLayer,
                            }}
                        />
                    </div>
                    <div className={styles.Airport}>
                        <Airport
                            initialValue={initialValue?.airport}
                            onValidate={onValidateItem}
                            placeholder={`${t('TRANS-Germany')} - ${t(
                                'TRANS-all-airports'
                            )}`}
                            label={
                                isS1S4MandatoryActive
                                    ? t('TRANS-flight-from')
                                    : t('TRANS-departure-airport')
                            }
                            customClassList={{
                                Input: styles.Input,
                                Label: styles.Label,
                                InputWrapper: classNames(styles.InputWrapper, {
                                    [styles.Error]:
                                        isFormValidate.isDepartureAirportEmpty,
                                }),
                                Layer: styles.AirportLayer,
                            }}
                        />
                    </div>
                    <ToggleFormElementProvider>
                        <div className={styles.Datepicker}>
                            <Datepicker
                                label={[t('TRANS-travel-period'), '']}
                                placeholder={[
                                    t('TRANS-departure'),
                                    t('TRANS-return'),
                                ]}
                                type={DatepickerType.Combined}
                                formType={formType}
                                initialValue={{
                                    departureDate: initialValue?.departureDate,
                                    returnDate: initialValue?.returnDate,
                                }}
                                onValidate={onValidateItem}
                                customClassList={{
                                    Input: styles.Input,
                                    InputWrapper: classNames(
                                        styles.InputWrapper,
                                        {
                                            [styles.Error]:
                                                isFormValidate.isTravelDatesEmpty,
                                        }
                                    ),
                                    Label: styles.Label,
                                    Layer: styles.DatepickerLayer,
                                }}
                            />
                        </div>
                        <div className={styles.Duration}>
                            <DurationLayer
                                initialValue={initialValue?.duration}
                                label={t('TRANS-travel-duration')}
                                placeholder={t('TRANS-please-choose')}
                                onValidate={onValidateItem}
                                customClassList={{
                                    Label: styles.Label,
                                    InputIconWrapper: styles.InputIconWrapper,
                                    InputWrapper: classNames(
                                        styles.InputWrapper,
                                        {
                                            [styles.Error]:
                                                isFormValidate.isDurationEmpty,
                                        }
                                    ),
                                    Input: styles.Input,
                                    Layer: styles.DurationLayer,
                                }}
                            />
                        </div>
                    </ToggleFormElementProvider>
                    <div className={styles.Occupancy}>
                        <OccupancyLayer
                            initialValue={initialValue?.occupancy}
                            placeholder={t('TRANS-please-choose')}
                            label={t('TRANS-traveller-and-room')}
                            customClassList={{
                                InputIconWrapper: styles.InputIconWrapper,
                                InputWrapper: styles.InputWrapper,
                                Input: styles.Input,
                                Layer: styles.OccupancyLayer,
                            }}
                        />
                    </div>
                    <SubmitContainer
                        onSubmit={onSubmit_}
                        customClass={styles.Cta}
                        withRefreshNote={false}
                    />
                </div>
            </div>
        </FormProvider>
    );
});

export default SingleLine;
